<script setup lang="ts">
// NPM
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import {
  BaseAvatar,
  BaseBadge,
  BaseButton,
  BaseIcon,
} from '@magma-app/magma-lapilli';
import RewardRetrievalTimeline from '@/components/rewards/RewardRetrievalTimeline.vue';

// Types
import type { PropType } from 'vue';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Stores
import { useCommonStore } from '@/stores/common';
import { useUserStore } from '@/stores/user';
import type {
  UserDashboardGetPurchasedRewardsResponseType,
  UserDashboardGetRewardsResponseType,
} from '@magma-app/magma-types/all';
const commonStore = useCommonStore();
const userStore = useUserStore();

// Plugins
const { t } = useI18n({ useScope: 'global' });

// Props
const props = defineProps({
  reward: {
    type: Object as PropType<
      | UserDashboardGetPurchasedRewardsResponseType[number]
      | (Omit<UserDashboardGetRewardsResponseType[number], 'status'> & {
          status: undefined;
        })
    >,
    required: true,
  },
  purchased: {
    type: Boolean,
    required: false,
  },
});

// Reactive variables
const isSaving = ref(false);

// Event emitters
const emit = defineEmits(['close']);

// Event listeners
const onClose = () => {
  emit('close');
};

const onContactAdmin = async () => {
  window.open(
    `mailto:${userStore.user?.organization?.notificationEmail}`,
    '_blank'
  );

  if (props.purchased) {
    AnalyticsHelper.trackEvent('ask_support', {
      event_properties: 'reward_requested',
      user_properties: 'helper',
    });
  }
};

const onPurchaseReward = (
  reward: UserDashboardGetRewardsResponseType[number]
) => {
  isSaving.value = true;
  commonStore.setManageRewardsModal({
    open: true,
    item: reward,
    role: 'reward-purchase',
    title: t('baseRewardChallengeDetails.title'),
  });

  AnalyticsHelper.trackEvent('buy_reward', {
    user_properties: 'helper',
  });
  isSaving.value = false;
};
</script>

<template>
  <div class="flex h-full w-full flex-col justify-between">
    <div>
      <div
        class="flex cursor-pointer content-center"
        :class="[purchased ? 'ml-4' : 'mb-4']"
        @click="onClose"
      >
        <BaseIcon color="#5F6C85" icon="chevron-left" />

        <span
          class="ml-1 text-mgm-txt-sm font-normal text-foreground-default"
          >{{
            purchased ? $t('generic.allRewards') : $t('generic.myRewards')
          }}</span
        >
      </div>

      <div class="mb-2 border-b border-border-subtle p-4 md:mb-4">
        <div class="mb-4 flex justify-between">
          <BaseAvatar
            :src="reward.pictureUrl ? reward.pictureUrl : '/img/reward_lg.png'"
            size="xl"
            shape="rounded"
          />

          <div class="ml-4 flex-1 truncate">
            <h3
              class="truncate text-mgm-txt-lg font-medium text-foreground-emphasis"
            >
              {{ reward.name }}
            </h3>

            <BaseBadge
              class="mt-2"
              :text="reward.points"
              size="md"
              color="grey"
              icon-type="icon"
              icon-name="star"
            />
          </div>
        </div>

        <p
          class="scrollbar-hide mt-4 max-h-28 overflow-scroll text-mgm-txt-sm font-normal text-foreground-default"
          v-html="reward.description"
        />
      </div>

      <RewardRetrievalTimeline
        :status="reward.status"
        :retrieval-instruction="reward.retrievalInstruction"
        :contact-email="userStore.user?.organization?.notificationEmail"
        :highlight-current-when-not-purchased="true"
        @contact-admin="onContactAdmin"
      />
    </div>
    <div v-if="!purchased" class="sticky bottom-0 bg-background-default py-6">
      <BaseButton
        class="w-full"
        :disabled="
          isSaving || !userStore.user || reward.points > userStore.user.balance
        "
        :loading="isSaving"
        :text="$t('generic.buy')"
        @click="
          onPurchaseReward(
            reward as UserDashboardGetRewardsResponseType[number]
          )
        "
      />
    </div>
  </div>
</template>
